import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";

/**
 * CommandPalette — cmd/ctrl+k modal that fuzzy-matches against
 * programs, projects in the current program (or all loaded programs),
 * recent chats, and project drawer actions.
 *
 * Pure presentational + minimal state — items are derived from props.
 * Parent (WorkspaceApp) is responsible for opening it (via keyboard shortcut)
 * and for choosing what's in scope based on the current route.
 */

const ITEM_ICON = {
  workspace: "🏠",
  program:   "🧬",
  project:   "📂",
  chat:      "💬",
  drawer:    "🗄",
  action:    "⚡",
};

// Platform-correct modifier glyph for the shortcut hint (⌘ on macOS, Ctrl
// elsewhere) — the palette opens with cmd/ctrl+K.
const MOD_KEY = (() => {
  if (typeof navigator === "undefined") return "Ctrl";
  const p = navigator.platform || navigator.userAgent || "";
  return /Mac|iPhone|iPod|iPad/i.test(p) ? "⌘" : "Ctrl";
})();

const matchScore = (query, text) => {
  if (!query) return 1;
  if (!text) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  // simple subsequence match
  let j = 0;
  for (let i = 0; i < t.length && j < q.length; i++) {
    if (t[i] === q[j]) j++;
  }
  return j === q.length ? 30 : 0;
};

export const buildPaletteItems = ({
  programs = [],
  projectsByProgram = {},
  recentChats = [],
  programId = null,
  projectId = null,
  query = "",
}) => {
  const items = [];

  items.push({ id: "act:workspace", kind: "workspace", label: "Go to Workspace", subtitle: "Plex / v2", to: "/v2" });

  programs.forEach((p) => {
    items.push({
      id: `prog:${p.id}`,
      kind: "program",
      label: p.name || p.id,
      subtitle: p.description || "Program",
      to: `/v2/p/${p.id}`,
    });
  });

  // Projects in the current program, plus any other loaded programs.
  Object.entries(projectsByProgram || {}).forEach(([progId, projects]) => {
    (projects || []).forEach((pr) => {
      items.push({
        id: `proj:${progId}/${pr.id}`,
        kind: "project",
        label: pr.name || pr.id,
        subtitle: `${progId} · ${pr.hypothesis || "Project"}`,
        to: `/v2/p/${progId}/${pr.id}`,
      });
    });
  });

  (recentChats || []).slice(0, 12).forEach((c) => {
    items.push({
      id: `chat:${c.id}`,
      kind: "chat",
      label: c.title || "Untitled chat",
      subtitle: `${c.programId || ""}${c.projectId ? " · " + c.projectId : ""}`,
      to: c.programId && c.projectId ? `/v2/p/${c.programId}/${c.projectId}/c/${c.id}` : null,
    });
  });

  if (programId && projectId) {
    [
      { id: "drawer:notebook", label: "Open Notebook", subtitle: "Findings + open questions + pinned" },
      { id: "drawer:data",     label: "Open Data",     subtitle: "Files + connected sources" },
      { id: "drawer:memory",   label: "Open Memory",   subtitle: "USER · PROJECT · PROGRAM" },
      { id: "drawer:team",     label: "Open Team",     subtitle: "Members + admin" },
    ].forEach((d) => items.push({ ...d, kind: "drawer" }));

    items.push({
      id: "act:new-chat",
      kind: "action",
      label: "New chat",
      subtitle: "In the current project",
      eventName: "workspace:new-chat",
    });
  }

  const scored = items
    .map((item) => {
      const labelScore = matchScore(query, item.label);
      const subtitleScore = matchScore(query, item.subtitle) * 0.4;
      return { item, score: Math.max(labelScore, subtitleScore) };
    })
    .filter(({ score }) => !query || score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 40)
    .map(({ item }) => item);

  return scored;
};

const KIND_LABEL = {
  workspace: "Go",
  program: "Program",
  project: "Project",
  chat: "Chat",
  drawer: "Drawer",
  action: "Action",
};

const PaletteItem = ({ item, active, onClick }) => (
  <li
    id={item.id}
    role="option"
    aria-selected={active}
    className={`cmdk__item${active ? " cmdk__item--active" : ""}`}
    onMouseDown={(e) => { e.preventDefault(); onClick?.(item); }}
    data-testid={`v2-cmdk-item-${item.id}`}
  >
    <span className="cmdk__item-icon" aria-hidden="true">{ITEM_ICON[item.kind] || "•"}</span>
    <span className="cmdk__item-text">
      <span className="cmdk__item-label">{item.label}</span>
      {item.subtitle && <span className="cmdk__item-sub">{item.subtitle}</span>}
    </span>
    <span className="cmdk__item-kind">{KIND_LABEL[item.kind] || item.kind}</span>
  </li>
);

const CommandPaletteBase = ({
  workspaceStore,
  open,
  onClose,
  programId = null,
  projectId = null,
  onDrawerOpen = null, // (id: "notebook" | "data" | "memory" | "team") => void
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const prevFocusRef = useRef(null);

  // On open: remember what was focused, reset + focus the input. On close
  // (effect cleanup): restore focus to the element that had it before opening,
  // so keyboard users aren't dumped at the top of the page.
  useEffect(() => {
    if (!open) return undefined;
    prevFocusRef.current = (typeof document !== "undefined") ? document.activeElement : null;
    setQuery("");
    setSelectedIdx(0);
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => { try { prevFocusRef.current?.focus?.(); } catch (_) { /* ignore */ } };
  }, [open]);

  const items = useMemo(() => buildPaletteItems({
    programs: workspaceStore?.programs || [],
    projectsByProgram: workspaceStore?.projectsByProgram || {},
    recentChats: workspaceStore?.recentChats || [],
    programId,
    projectId,
    query,
  }), [workspaceStore?.programs, workspaceStore?.projectsByProgram, workspaceStore?.recentChats, programId, projectId, query]);

  useEffect(() => { setSelectedIdx(0); }, [query]);

  // Keep the active option scrolled into view as the selection moves through a
  // long (up to 40-item) list — arrow-key nav must never leave it off-screen.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(".cmdk__item--active")?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIdx, open, items.length]);

  const choose = (item) => {
    if (!item) return;
    onClose?.();
    if (item.to) {
      navigate(item.to);
      return;
    }
    if (item.kind === "drawer") {
      const drawerId = item.id.split(":")[1];
      // Prefer an explicit handler if the parent wired one; otherwise fall back
      // to the global workspace:open-drawer event that ProjectHeader already listens
      // for — so drawer commands work even though WorkspaceApp doesn't pass onDrawerOpen
      // (previously these were dead no-ops).
      if (onDrawerOpen) {
        onDrawerOpen(drawerId);
      } else if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("workspace:open-drawer", { detail: { drawer: drawerId } }));
      }
      return;
    }
    if (item.eventName && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(item.eventName));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(items[selectedIdx]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    } else if (e.key === "Tab") {
      // The search input is the only focusable element inside the palette
      // (options are arrow-key navigated, not tab-stops) — trap Tab so focus
      // can't escape behind the backdrop.
      e.preventDefault();
      inputRef.current?.focus();
    }
  };

  if (!open) return null;

  return (
    <div className="cmdk-root" data-testid="v2-cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
      <div className="cmdk-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="cmdk-card" onKeyDown={onKeyDown}>
        <div className="cmdk-search">
          <span className="cmdk-search__icon" aria-hidden="true">{MOD_KEY}</span>
          <input
            ref={inputRef}
            className="cmdk-search__input"
            placeholder="Type to search programs, projects, chats…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="v2-cmdk-input"
            aria-controls="cmdk-list"
            aria-activedescendant={items[selectedIdx]?.id || null}
          />
          <span className="cmdk-search__hint">esc</span>
        </div>
        <ul
          ref={listRef}
          id="cmdk-list"
          role="listbox"
          className="cmdk__list"
          data-testid="v2-cmdk-list"
        >
          {items.length === 0 ? (
            <li className="cmdk__empty" data-testid="v2-cmdk-empty">
              No matches. Try a different keyword.
            </li>
          ) : items.map((item, i) => (
            <PaletteItem
              key={item.id}
              item={item}
              active={i === selectedIdx}
              onClick={choose}
            />
          ))}
        </ul>
        <footer className="cmdk__foot">
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
        </footer>
      </div>
    </div>
  );
};

const CommandPalette = inject("workspaceStore")(observer(CommandPaletteBase));
export default CommandPalette;
export { CommandPaletteBase };
